import { useTranslate } from '@tolgee/react';
import { ReturnActionsProps } from './types';
import { Button, message } from 'antd';
import { confirmReturn, disputeReturn, KwaiOperate, refuse } from 'services/api/return';
import { useRequest } from 'ahooks';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';
import StatusButton from 'components/StatusButton';
import { formatNumber } from 'utils/numberFormat';
import useModal from 'hooks/useModal';
import { RefuseFormModal, RefuseFormModalProps } from 'components/Refund/Refuse';

export const ReturnActionKwai = (props: ReturnActionsProps) => {
  const {
    row: {
      unit,
      refundAmount,
      internalStoreId,
      externalStoreId,
      orderSn,
      returnSn,
      operate = [],
      returnItemData,
    },
    platform,
    refreshList,
  } = props;

  const { t } = useTranslate();

  const { runAsync: confirmSubmit } = useRequest(
    (operate: KwaiOperate) =>
      confirmReturn({
        externalStoreId,
        externalTradeId: orderSn,
        returnSn,
        externalType: platform,
        operate,
      }),
    {
      manual: true,
      onSuccess() {
        refreshList();
      },
      onError(err: any) {
        message.error(getApiErrorCodeMsg(err.response));
      },
    },
  );

  const { visible, show, hide, meta } = useModal<Omit<RefuseFormModalProps, 'onSubmit'>>();

  return (
    <>
      {operate.includes('CONFIRM_RETURN_REFUND') && (
        <StatusButton
          size="small"
          popConfirmProps={{
            title: t('确认同意买家的退货申请吗？'),
          }}
          onClick={() => confirmSubmit('CONFIRM_RETURN_REFUND')}
        >
          {t('同意退货')}
        </StatusButton>
      )}
      {operate.includes('CONFIRM_ONLY_REFUND') && (
        <StatusButton
          size="small"
          popConfirmProps={{
            title: t('确认同意买家的退款申请吗？'),
          }}
          onClick={() => confirmSubmit('CONFIRM_ONLY_REFUND')}
        >
          {t('同意退款')}
        </StatusButton>
      )}
      {operate.includes('FULL_REFUND') && (
        <StatusButton
          size="small"
          popConfirmProps={{
            title: t('trade.全额退款提示', {
              money: `${unit}${formatNumber(refundAmount, '0,0.00')}`,
            }),
          }}
          onClick={() => confirmSubmit('FULL_REFUND')}
        >
          {t('改为仅退款并同意')}
        </StatusButton>
      )}
      {operate.includes('REFUSE') && (
        <>
          <Button
            size="small"
            onClick={() => {
              show({
                internalStoreId,
                initialValues: {
                  externalStoreId,
                  returnSn,
                  returnOrderItemId: returnItemData.map((_) => _.returnOrderItemId) as any,
                  image: [],
                  reasonId: undefined,
                  comment: '',
                  operate: 'REFUSE',
                },
              });
            }}
          >
            {t('tableColumn.拒绝')}
          </Button>
          {meta && (
            <RefuseFormModal
              visible={visible}
              onCancel={() => hide()}
              {...meta}
              onSubmit={(values, helpers) => {
                const { image, reasonId = '', comment = '', ...others } = values;

                return refuse({
                  ...others,
                  reasonId,
                  comment,
                  image: image
                    .map((file) => file.response?.data?.url || file.url)
                    .filter((_) => _) as string[],
                })
                  .then(() => {
                    refreshList();

                    message.success(t('ViewRefund.refundT.已拒绝'));

                    hide();

                    helpers.resetForm();
                  })
                  .catch((e) => {
                    message.error(
                      getApiErrorCodeMsg(e.response, 'ViewRefund.拒绝失败，请稍后再试'),
                    );
                  });
              }}
            />
          )}
        </>
      )}
      {operate.includes('DISPUTE') && (
        <StatusButton
          size="small"
          onClick={() => {
            return disputeReturn({
              platform: 'KWAI',
              returnSn,
              operate: 'DISPUTE',
            })
              .then(() => {
                refreshList?.();
                message.success(t('phrase.保存成功'));
              })
              .catch(() => {
                message.error(t('phrase.保存失败，请稍后再试'));
              });
          }}
        >
          {t('申请平台介入')}
        </StatusButton>
      )}
    </>
  );
};
